import { formatBytesPerSec, formatDurationToString, pluralize } from '@lib/utils/units';
import { FileUploadStatus } from '@stores/UploadManager/UploadManager';
import {
  computeBytesBasedUploadStats,
  readSmoothedUploadStats,
  UploadEtaState,
} from '@stores/UploadManager/uploadStats';
import { now as mobxNow } from 'mobx-utils';

/**
 * Shared utilities for upload speed and ETA calculations.
 * Used by both grid view (MediaCard) and table view (StatusCell).
 */

export interface UploadStats {
  /** Upload speed in bytes per second */
  speedBps: number;
  /** Formatted ETA text (e.g., "2 min left") */
  etaText: string;
  /** Combined subtext with speed and ETA (e.g., "1.5 MB/s • 2 min left") */
  subtext: string;
}

/**
 * Calculate upload statistics including speed and ETA.
 *
 * @param uploadStatus - The file upload status object
 * @param progress - Upload progress percentage (0-100)
 * @returns Upload statistics with speed and ETA information
 */
export function calculateUploadStats(
  uploadStatus: FileUploadStatus | null | undefined,
  progress: number,
): UploadStats {
  if (!uploadStatus) {
    return { speedBps: 0, etaText: '', subtext: '' };
  }

  // Use the same bytes-based estimator as the panel banner / BatchCards so the
  // per-file pill and the header never disagree (the per-file simple-eta used to
  // diverge wildly — recent-rate vs average-since-start). The passed `progress`
  // overrides the stored value since callers track live progress.
  const nowMs = mobxNow(500);
  const rawStats = computeBytesBasedUploadStats(
    [
      {
        file: uploadStatus.file,
        progress,
        uploadStartedAt: uploadStatus.uploadStartedAt,
        resumedProgress: uploadStatus.resumedProgress,
        progressSamples: uploadStatus.progressSamples,
      },
    ],
    nowMs,
  );
  const stats = readSmoothedUploadStats(uploadStatus.smoothedStats ?? null, rawStats, nowMs);
  const timeLeftSec = stats?.etaSeconds;
  const speedBps = stats?.speedBps ?? 0;

  const etaText =
    timeLeftSec && isFinite(timeLeftSec)
      ? `${formatDurationToString(Math.floor(timeLeftSec + 1), { precision: 0 })} left`
      : '';

  // Only join speed + ETA when there's an ETA to show. At 100% (remaining 0 ⇒
  // eta 0 ⇒ empty etaText) speed can still be > 0, which would otherwise leave a
  // dangling "X MB/s • " separator during the success transition.
  const subtext = speedBps > 0 && etaText ? `${formatBytesPerSec(speedBps)} • ${etaText}` : etaText;

  return { speedBps, etaText, subtext };
}

/**
 * Label for the library header's upload line, derived from the aggregate ETA
 * (`uploadManager.globalUploadEta`). Shared by the desktop and mobile headers so
 * the wording can't drift.
 *
 * The switch is exhaustive so a new state can't be silently absorbed into the
 * wrong branch:
 * - `not-started` is the ONLY case that may say "preparing" — bytes are not
 *   moving yet. Saying it once bytes are in flight is a bug, not a nuance.
 * - `warming-up` and `stalled` deliberately share one neutral label HERE, so
 *   the only visible effect of a headline stall is that the countdown stops
 *   being shown. A stall is usually transient and self-recovering, and the
 *   header is ambient chrome the user isn't looking at — shouting about the
 *   connection from it would be alarming out of proportion to the event.
 *   Stall-specific copy lives on the batch surfaces (BatchCard, and the panel
 *   banner), which are driven by `deriveBatchState`'s `'stalled'` — a separate
 *   signal on a shorter fuse, NOT this `eta.kind`. Nothing branches on
 *   `eta.kind === 'stalled'`; don't go looking for it.
 * - "finishing up" is reserved for a genuinely small finite ETA (bytes
 *   essentially done, server finalizing) — never for a missing estimate.
 */
export function getUploadHeaderLabel(eta: UploadEtaState, uploadingCount: number): string {
  const files = pluralize(uploadingCount, 'file');
  switch (eta.kind) {
    case 'not-started':
      return `Preparing to upload ${files}...`;
    case 'warming-up':
    case 'stalled':
      return `Uploading ${files}...`;
    case 'estimate':
      return eta.seconds > 1
        ? `Uploading ${files}, done in about ${formatDurationToString(Math.floor(eta.seconds + 1), { detail: 1, precision: 0 })}`
        : `Uploading ${files}, finishing up`;
  }
}

/**
 * Get appropriate subtext for upload status, handling edge cases.
 * Returns just the ETA text (no speed) - suitable for compact displays like table view.
 *
 * @param uploadStatus - The file upload status object
 * @param progress - Upload progress percentage (0-100)
 * @returns Subtext string for display (etaText only, or "Finishing up..." at 100%)
 */
export function getUploadSubtext(
  uploadStatus: FileUploadStatus | null | undefined,
  progress: number,
): string {
  const stats = calculateUploadStats(uploadStatus, progress);

  // Show "Finishing up..." when progress is 100% but no ETA
  if (progress === 100 && !stats.etaText) {
    return 'Finishing up...';
  }

  return stats.etaText;
}
